export const color = `
  id
  handle
  label: field(key: "label") {
    value
  }
  color: field(key: "color") {
    value
  }
  image: field(key: "image") {
    reference {
      ... on MediaImage {
        alt
        previewImage {
          url
        }
      }
    }
  }
`;

const badge = `
  badge: metafield(namespace: "content", key: "product_badge") {
    reference {
      ... on Metaobject {
        text: field(key: "text") {
          value
        }
        color: field(key: "color") {
          value
        }
        showInCart: field(key: "show_in_cart") {
          value
        }
        position: field(key: "position") {
          value
        }
      }
    }
  }
`;

const variantFields = `
id
title
availableForSale
quantityAvailable
price {
  amount
  currencyCode
}
compareAtPrice {
  amount
  currencyCode
}
selectedOptions {
  name
  value
}
image {
  altText
  url
}
sku
components: metafield(namespace: "details", key: "components") {
  references(first: 100) {
    edges {
      node {
        ... on ProductVariant {
          id
          sku
          title
          quantityAvailable
          product {
            title
          }
        }
      }
    }
  }
}
multiComponents: metafield(namespace: "details", key: "multi_components") {
  references(first: 100) {
    edges {
      node {
        ... on Metaobject {
          variant: field(key: "variant") {
            reference {
              ... on ProductVariant {
                id
                sku
                title
                quantityAvailable
                product {
                  title
                }
              }
            }
          }
          quantity: field(key: "quantity") {
            value
          }
        }
      }
    }
  }
}
${badge}
cargoReady: metafield(namespace: "details", key: "cargo_ready") {
  value
}
dnr: metafield(namespace: "details", key: "dnr") {
  value
}
backorderDate: metafield(namespace: "details", key: "backorder_date") {
  value
}
lowStockThreshold: metafield(namespace: "details", key: "low_stock_threshold") {
  value
}
retailPrice: metafield(namespace: "details", key: "retail_price") {
  value
}
materialGroup: metafield(namespace: "configurability", key: "material_group") {
  value
}
madeToOrder: metafield(namespace: "configurability", key: "made_to_order") {
  value
}
auStockMessage: metafield(namespace: "content", key: "stock_message") {
  value
}
hideOnPdp: metafield(namespace: "content", key: "hide_on_pdp") {
  value
}
earlyAccess: metafield(namespace: "custom", key: "early_access") {
  value
}
maxQuantityThreshold: metafield(namespace: "details", key: "max_quantity_threshold") {
  value
}
saleItem: metafield(namespace: "custom", key: "reporting_sale_item") {
  value
}
relatedProductsSwatches: metafield(namespace: "content", key: "related_products") {
  references(first: 1) {
    edges {
      node {
        ... on Product {
          id
          handle
          onlineStoreUrl
          featuredImage {
            altText
            url
          }
          swatchType: metafield(namespace: "details", key: "swatch_type") {
            value
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 1) {
            nodes {
              id
              price {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  }
}
`;

const productBaseFields = `
id
title
handle
onlineStoreUrl
priceRange {
  minVariantPrice {
    amount
    currencyCode
  }
}
compareAtPriceRange {
  minVariantPrice {
    amount
    currencyCode
  }
}
featuredImage {
  altText
  url
}
productType
vendor
options {
  name
  optionValues {
    name
    swatch {
      image {
        alt
        previewImage {
          url
        }
      }
      color
    }
  }
}
`;

export const product = `
  ${productBaseFields}
  variants(first: 250) {
    edges {
      node {
        ${variantFields}
      }
    }
  }
  materialGroup: metafield(namespace: "configurability", key: "material_group") {
    value
  }
  colors: metafield(namespace: "shopify", key: "color-pattern") {
    references(first: 100) {
      nodes {
        ... on Metaobject {
          id
          handle
        }
      }
    }
  }
  bundleColor: metafield(namespace: "details", key: "bundle_color") {
    reference {
      ... on Metaobject {
        ${color}
      }
    }
  }
  bundleColorLabel: metafield(namespace: "details", key: "bundle_color_label") {
    value
  }
  discontinuedRedirect: metafield(namespace: "content", key: "discontinued_redirect") {
    reference {
      ... on Product {
        onlineStoreUrl
        handle
      }
    }
  }
  ${badge}
  setProducts: metafield(namespace: "details", key: "set_products") {
    references(first: 10) {
      edges {
        node {
          ... on Metaobject {
            product: field(key: "product") {
              reference {
                ... on Product {
                  handle
                  id
                }
              }
            }
            quantity: field(key: "quantity") {
              value
            }
            defaultVariant: field(key: "default_variant") {
              value
            }
          }
        }
      }
    }
  }
`;

export const productGroup = `
    group: metafield(namespace: "details", key: "group") {
      reference {
        ... on Metaobject {
          id
          handle
          products: field(key: "products") {
            references(first: 100) {
						  nodes {
                ... on Product {
                  ${product}
                }
              }
            }
          }
          bundleColors: field(key: "bundle_colors") {
            references(first: 100) {
              nodes {
                ... on Metaobject {
                  ${color}
                }
              }
            }
          }
        }
      }
    }
`;

export const cart = `
  id
  delivery {
    addresses {
      id
      selected
      oneTimeUse
      address {
        ... on CartDeliveryAddress {
          firstName
          lastName
          company
          address1
          address2
          city
          provinceCode
          zip
          countryCode
        }
      }
    }
  }
`;

export const discountMessage = `
  ... on Metaobject {
    handle
    markets: field(key: "markets") {
      value
    }
    customerGroups: field(key: "customer_groups") {
      references(first: 10) {
        nodes {
          ... on Metaobject {
            value: field(key: "group") {
              value
            }
          }
        }
      }
    }
    customerTags: field(key: "customer_tags") {
      value
    }
    title: field(key: "title") {
      value
    }
    bundleTitle: field(key: "bundle_title") {
      value
    }
    message: field(key: "message") {
      value
    }
    successMessage: field(key: "success_message") {
      value
    }
    minimumQuantityTiers: field(key: "minimum_quantity_tiers") {
      value
    }
    tierType: field(key: "tier_type") {
      value
    }
    percentageOffTiers: field(key: "percentage_off_tiers") {
      value
    }
  }
`;
